<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    dayjs.extend(_isToday)
    import AccountSelect from './accounts/account-select.svelte'
    import FinancialImportOfxItem from './financial-import-ofx-item.svelte'
    import { OFXParserService } from '../../modules/utils/ofx-parser.service'
    import type { Category } from '../../modules/financialApi/categories/category'
    import { EntryTypeEnum } from '../../modules/financialApi/entries/entry.enum'
    import type { CreateEntryDto } from '../../modules/financialApi/entries/dto/create-entry.dto'
    import type { Account } from '../../modules/financialApi/accounts/account'
    import type { Entry } from '../../modules/financialApi/entries/entry'

    const dispatch = createEventDispatcher()

    let categoriesCredit: Category[] = []
    let categoriesDebit: Category[] = []

    let show = false
    let step = 1
    onMount(async () => {
        categoriesCredit = await window.api.financials.categories.getAll({ cd: EntryTypeEnum.credit })
        categoriesDebit = await window.api.financials.categories.getAll({ cd: EntryTypeEnum.debit })
    })

    async function saveEntity() {
        upsertEntity()
    }

    function validateAll() {
        for (let entity of entries) {
            if (!validateDto(entity)) return false
        }
        return true
    }

    async function upsertEntity() {
        try {
            if (show) return
            show = true
            entries = entries.filter((el) => !!el)
            if (!validateAll()) return false
            // entries é alimentado pelo loadOFX
            await window.api.financials.entries.bulkUpsert(entries)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function close() {
        for (const item of entries) {
            if (!item.categoryId) {
                if (!confirm('Nem todos os itens foram importados.\nDeseja mesmo sair?')) {
                    return
                }
                break
            }
        }
        dispatch('change')
    }

    function validateDto(dto) {
        if (!dto.accountId) {
            evaluateError('Informe a conta')
            return false
        }
        if (!dto.categoryId) {
            evaluateError('É necessário categorizar todas as despesas antes de salvar')
            return false
        }
        if (!dto.value) {
            evaluateError('Informe o valor')
            return false
        }
        return true
    }

    function evaluateError(error) {
        window.error(error)
    }

    let file
    let fileContent: string = null
    function fileChanged() {
        fileContent = null
        if (!file.files || !file.files[0]) {
            return
        }
        let filePath = file.files[0]
        let reader = new FileReader()
        reader.readAsText(filePath, 'utf-8')
        let reload = false
        reader.onload = (e) => {
            let content: string = reader.result as string
            if (content != null) {
                if (reload === false) {
                    let knownChars = ['�', '�', '�', '�', '�', '�', '�', '�', '�', '�']
                    for (const chr of knownChars) {
                        if (content.includes(chr)) {
                            reload = true
                            reader.readAsText(filePath, 'iso-8859-1')
                            return
                        }
                    }
                }
                fileContent = content
            }
        }
    }

    async function onEntryChanged(data) {
        await window.api.financials.entries.upsert(data.detail)
    }

    let entries: CreateEntryDto[] = []
    let account: Account = null
    async function loadOFX() {
        if (!account?.accountId) {
            evaluateError('Selecione uma conta bancária')
            return
        }
        if (!fileContent) {
            evaluateError('Selecione um arquivo OFX')
            return
        }
        const ofx = new OFXParserService()
        let data = ofx.toJSON(fileContent)
        // num conta
        console.log('ofx', data)
        console.log('banco', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.BRANCHID)
        console.log('conta', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.ACCTID)
        console.log('entries', data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST)
        let accountNumber: string = data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKACCTFROM.ACCTID
        if (
            accountNumber.substring(accountNumber.length - 4) !==
            account.account.replace('-', '').substring(account.account.length - 4)
        ) {
            evaluateError([
                'O arquivo não pertence a esta conta.',
                'Caso o arquivo esteja correto verifique se o nome do banco e o número da conta estão corretos no cadastro da conta selecionada.',
            ])
            return
        }

        entries = []
        if (data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN.length === 0) {
            window.alert('O arquivo está vazio')
            return
        }
        let startDate = data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN[0].DTPOSTED
        let endDate =
            data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN[
                data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN.length - 1
            ].DTPOSTED
        let persisteds: Entry[] = await window.api.financials.entries.getAll({
            from: startDate,
            to: endDate,
        })
        for (const item of data.OFX.BANKMSGSRSV1.STMTTRNRS.STMTRS.BANKTRANLIST.STMTTRN) {
            if (dayjs(item.DTPOSTED).isToday()) continue // não importa o dia atual, pois o OFX é parcial e pode gerar problemas com o ID único
            const persisted: Entry = persisteds.find((el) => el.externalId === item._id)
            try {
                let entry: CreateEntryDto = {
                    accountId: account.accountId,
                    description:
                        item.MEMO.toString().charAt(0).toUpperCase() + item.MEMO.toString().slice(1).toLowerCase(),
                    categoryId: persisted?.categoryId,
                    paymentAt: item.DTPOSTED,
                    personId: persisted?.personId,
                    type: item.TRNAMT > 0 ? EntryTypeEnum.credit : EntryTypeEnum.debit,
                    value: item.TRNAMT > 0 ? item.TRNAMT : item.TRNAMT * -1,
                    source: 'ofx',
                    reference: item.DTPOSTED,
                    externalId: item._id,
                }
                entries.push(entry)
            } catch (error) {
                console.error('Import failed', error)
                console.error('Import item', item)
                throw error
            }
        }
        entries = entries
        step = 2
    }
</script>

{#if step === 1}
    <div class="root m-bottom-sm">
        <div class="grid-act">
            <div class="spacer" />
            {#if window.store.token.permissions.financial.entries.write}
                <button class="btn btn-primary" on:click={() => loadOFX()}>
                    <Fa icon={faArrowRight} /> Avançar
                </button>
            {/if}
        </div>
        <div class="grid-form">
            <div class="row">
                <div class="col">
                    <div>Importe rapidamente todo seu extrato bancário através de um arquivo OFX.</div>
                    <div class="text-muted mb-3">
                        Os bancos fornecem seu extrato para serem baixados no formato OFX, que podem ser importados pelo
                        Zandu.<br />
                        Para baixar seu extrato bancário no formato OFX para o seu computador:<br />
                        <ul>
                            <li>Acesse seu Internet Banking</li>
                            <li>Vá até a área de extratos</li>
                            <li>Salve / exporte seu extrato no formato OFX (OFX Money)</li>
                        </ul>
                        Após baixar do Internet Banking você poderá importá-lo aqui.
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <AccountSelect label="Conta a ser importada" bind:account />
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <input
                        class="form-control"
                        type="file"
                        accept=".ofx"
                        id="formFile"
                        on:change={fileChanged}
                        bind:this={file}
                    />
                </div>
            </div>
        </div>
    </div>
{/if}

{#if step === 2}
    <div class="root m-bottom-sm">
        <div class="grid-act">
            <div class="spacer" />
            <!-- {#if window.store.token.permissions.financial.entries.write}
                <button class="btn btn-primary" on:click={() => saveEntity()}>
                    <Fa icon={faSave} /> Salvar
                </button>
            {/if} -->
            <button class="btn btn-primary" on:click={() => close()}>
                <Fa icon={faCheck} /> Concluir
            </button>
        </div>

        <div class="grid-form px-2">
            <div class="row align-middle">
                <div class="col-1">Data</div>
                <div class="col nowrap">Descrição</div>
                <div class="col-1 text-end">Valor</div>
                <div class="col">Categoria</div>
                <div class="col-2" />
            </div>

            {#each entries as entry}
                <FinancialImportOfxItem
                    {entry}
                    categories={entry.type === EntryTypeEnum.credit ? categoriesCredit : categoriesDebit}
                    on:change={onEntryChanged}
                />
            {/each}
        </div>
    </div>
{/if}

<style>
    .row {
        padding: 0px 16px;
    }

    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
        .grid-act {
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .grid-act .btn {
            max-width: 120px;
            flex-grow: 1;
        }
    }
</style>
